import { z } from 'zod';
import { UuidString } from '../../primitives.js';

/**
 * BE-3 — история правок задачи: per-field diff + total-count для бейджа таба.
 *
 * Structural-only (i18n invariant): field — стабильный snake_case-ключ поля
 * (FE локализует), from/to — сырые значения из request_payload (unknown,
 * pass-through; FE рендерит по field). actor_user_name — denorm bundle (как в
 * audit-эндпоинте; единственный denorm — имя, остальное structural).
 */

/** Один изменённый field для события `task_module_task.updated`. */
export const AuditChange = z.object({
  field: z.string().min(1),
  /** Прежнее значение (если выводимо). Pass-through, FE рендерит по field. */
  from: z.unknown().optional(),
  to: z.unknown(),
});
export type AuditChange = z.infer<typeof AuditChange>;

/** Нормализованное audit-событие задачи (denorm actor name + per-field diff). */
export const AuditEventView = z.object({
  id: UuidString,
  occurred_at: z.string().datetime({ offset: true }),
  actor_user_id: UuidString.nullable(),
  actor_user_name: z.string().nullable(),
  /** BE-C4 — кто инициатор: человек / агент / система. FE рендерит «Система»
   *  для не-human без имени (вместо пустого «—»). */
  actor_kind: z.enum(['human', 'agent', 'system']),
  actor_role: z.string().nullable(),
  action: z.string(),
  resource_type: z.string(),
  resource_id: UuidString.nullable(),
  tier: z.number().int(),
  tool_name: z.string(),
  /** Сырой payload запроса (pass-through). FE предпочитает `changes`. */
  request_payload: z.unknown(),
  response_summary: z.unknown(),
  status: z.string(),
  error_code: z.string().nullable(),
  /** Per-field diff для `task_module_task.updated`; пусто для прочих action. */
  changes: z.array(AuditChange),
});
export type AuditEventView = z.infer<typeof AuditEventView>;

export const AuditEventListResponse = z.object({
  /** BE-C5 — версия контракта (инвариант: schema_version в backbone-ответах). */
  schema_version: z.string(),
  audit_events: z.array(AuditEventView),
  next_cursor: z.string().nullable(),
  /** Полное число событий по задаче (для бейджа таба «История»). */
  total: z.number().int().nonnegative(),
});
export type AuditEventListResponse = z.infer<typeof AuditEventListResponse>;
